import { default as React, useEffect, useMemo, useState } from 'react';
import {
  DevHubAutocomplete,
  DevHubAutocompleteChangeReason,
  DevHubBox,
  DevHubChip,
  DevHubCheckbox,
  DevHubTextField,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { gql, useLazyQuery } from '@apollo/client';

export interface BusinessCapability {
  name: string;
  sysId: string;
  hierarchyId?: string | null;
  children?: Omit<BusinessCapability, 'children'>[];
}

const IOPBusinessCapabilitiesQuery = gql`
  query GetIOPBusinessCapabilities {
    resources {
      iopBusinessCapabilities {
        name
        sysId
        hierarchyId
        children {
          sysId
          name
          hierarchyId
        }
      }
    }
  }
`;

interface Props {
  className?: string;
  helperText?: string;
  label?: string;
  selectedCapabilities: BusinessCapability[];
  setSelectedCapabilities: React.Dispatch<
    React.SetStateAction<BusinessCapability[]>
  >;
  required?: boolean;
}

/**
 * Two-level cascading selector for IOP Business Capabilities.
 * First selects a parent category, then selects leaf capabilities within it.
 * Selections accumulate across parent switches.
 */
export function BusinessCapabilitiesSelect({
  className,
  helperText = 'Select one or more business capabilities',
  label = 'Business Capabilities',
  selectedCapabilities = [],
  setSelectedCapabilities,
  required = false,
}: Props) {
  const [parentCapabilities, setParentCapabilities] = useState<
    BusinessCapability[]
  >([]);
  const [selectedParent, setSelectedParent] =
    useState<BusinessCapability | null>(null);

  const [getCapabilities, { loading, error }] = useLazyQuery(
    IOPBusinessCapabilitiesQuery,
    {
      errorPolicy: 'all',
    },
  );

  useEffect(() => {
    getCapabilities({})
      .then(result => {
        const data: BusinessCapability[] | undefined =
          result?.data?.resources?.iopBusinessCapabilities;
        if (data) {
          const parents = data.filter(
            bc => bc.children && bc.children.length > 0,
          );
          setParentCapabilities(parents);
        }
      })
      .catch(err => {
        // eslint-disable-next-line no-console
        console.error('Error fetching business capabilities', err);
      });
  }, [getCapabilities]);

  useEffect(() => {
    if (parentCapabilities.length === 0 || selectedCapabilities.length === 0) {
      return;
    }
    const firstSysId = selectedCapabilities[0].sysId;
    const matchingParent = parentCapabilities.find(p =>
      p.children?.some(c => c.sysId === firstSysId),
    );
    if (matchingParent) {
      setSelectedParent(matchingParent);
    }
  }, [parentCapabilities, selectedCapabilities]);

  const childCapabilities = useMemo(
    () => selectedParent?.children ?? [],
    [selectedParent],
  );

  // Only the children belonging to the current parent, for the child selector value
  const selectedChildren = useMemo(
    () =>
      selectedCapabilities.filter(sc =>
        childCapabilities.some(c => c.sysId === sc.sysId),
      ),
    [selectedCapabilities, childCapabilities],
  );

  const handleParentChange = (
    _event: Object,
    value: BusinessCapability | null,
    _reason: DevHubAutocompleteChangeReason,
  ) => {
    setSelectedParent(value);
  };

  const handleChildChange = (
    _event: Object,
    value: BusinessCapability[],
    reason: DevHubAutocompleteChangeReason,
  ) => {
    if (reason === 'clear') {
      // Remove only this parent's children from the overall selection
      const parentChildSysIds = new Set(childCapabilities.map(c => c.sysId));
      setSelectedCapabilities(prev =>
        prev.filter(sc => !parentChildSysIds.has(sc.sysId)),
      );
      return;
    }
    // Merge: keep selections from other parents, replace this parent's selections
    const parentChildSysIds = new Set(childCapabilities.map(c => c.sysId));
    setSelectedCapabilities(prev => [
      ...prev.filter(sc => !parentChildSysIds.has(sc.sysId)),
      ...value,
    ]);
  };

  if (error) {
    return (
      <DevHubTextField
        className={className}
        error
        label="Error"
        defaultValue="No Business Capabilities Available"
        variant="outlined"
      />
    );
  }

  return (
    <DevHubBox className={className}>
      <DevHubAutocomplete
        loading={loading}
        loadingText="Loading.."
        options={parentCapabilities}
        value={selectedParent}
        onChange={handleParentChange}
        getOptionLabel={(option: BusinessCapability) => option.name}
        getOptionSelected={(
          option: BusinessCapability,
          value: BusinessCapability,
        ) => option.sysId === value.sysId}
        renderOption={(
          option: BusinessCapability,
          { selected }: { selected: boolean },
        ) => (
          <>
            <DevHubCheckbox checked={selected} />
            {option.name}
          </>
        )}
        renderInput={params => (
          <DevHubTextField
            {...params}
            label="Business Capability Category"
            margin="normal"
            variant="outlined"
            InputProps={params.InputProps}
          />
        )}
      />
      {selectedParent && (
        <DevHubBox>
          <DevHubTypography variant="caption" color="textSecondary">
            Select capabilities under &quot;{selectedParent.name}&quot;
          </DevHubTypography>
          <DevHubAutocomplete
            multiple
            loading={loading}
            loadingText="Loading.."
            options={childCapabilities}
            value={selectedChildren}
            onChange={handleChildChange}
            disableCloseOnSelect
            getOptionLabel={(option: BusinessCapability) => option.name}
            getOptionSelected={(
              option: BusinessCapability,
              value: BusinessCapability,
            ) => option.sysId === value.sysId}
            renderOption={(
              option: BusinessCapability,
              { selected }: { selected: boolean },
            ) => (
              <>
                <DevHubCheckbox checked={selected} />
                {option.name}
              </>
            )}
            renderTags={(
              tagValue: BusinessCapability[],
              getTagProps: Function,
            ) =>
              tagValue.map((option: BusinessCapability, index: number) => (
                <DevHubChip
                  label={option.name}
                  size="small"
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={params => (
              <DevHubTextField
                {...params}
                label={label}
                InputLabelProps={{ required }}
                helperText={helperText}
                margin="normal"
                variant="outlined"
                required={required && selectedChildren.length === 0}
                InputProps={params.InputProps}
              />
            )}
          />
        </DevHubBox>
      )}
    </DevHubBox>
  );
}
